import React, { useState } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
  NavLink,
  useLocation
} from "react-router-dom";
import Home from './components/Home';
import About from './components/About';
import Contact from './components/Contact';
import History from './components/History';
import PCBuilding from './components/PCBuilding';
import Blog from './components/Blog';
import PostDetails from './components/PostDetails';
import LoadingIntro from './components/LoadingIntro';
import CategoryPage from './components/CategoryPage';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFacebookF, faInstagram, faTiktok } from '@fortawesome/free-brands-svg-icons';
import { faDesktop, faGamepad, faMobileScreen } from '@fortawesome/free-solid-svg-icons';

const SITE_URL = 'https://gameoncosham.co.uk';
const DEFAULT_IMAGE = `${SITE_URL}/images/og-image.webp`;

const shouldShowLoadingIntro = () => {
  const params = new URLSearchParams(window.location.search);
  if (params.get('intro') === '1') return true;
  return window.sessionStorage.getItem('gameonIntroSeen') !== 'true';
};

const pageMeta = {
  '/': { title: 'Game On Cosham | Games, Consoles, Tech & Gaming PCs', description: 'Buy, sell and trade consoles, games, phones, laptops and gaming PCs at Game On Cosham, your independent gaming shop in Portsmouth.' },
  '/about': { title: 'About Game On Cosham | Independent Gaming Shop', description: 'Meet the team behind Game On Cosham, an independent Portsmouth shop for consoles, games, technology, gaming PCs and local trade-ins.', image: '/images/2026/about/about-hero.webp' },
  '/pc-building': { title: 'Gaming PCs in Cosham | PC Builds & Upgrades', description: 'Shop tested gaming PCs in Cosham and get practical advice on PC builds, graphics cards, storage and performance upgrades.', image: '/images/2026/pc-building/pc-building-hero.webp' },
  '/history': { title: 'History of Video Games | Game On Cosham', description: 'Explore more than 50 years of video game history, from early home consoles and 16-bit classics to online play and modern gaming PCs.', image: '/images/2026/history/era-1985-1994.png' },
  '/contact': { title: 'Contact Game On Cosham | Visit Our Portsmouth Shop', description: 'Visit Game On at 1-2 Crown Buildings in Cosham, Portsmouth. Find opening hours, directions and contact details for trade-ins and stock enquiries.' },
  '/consoles': { title: 'Consoles in Cosham | PlayStation, Xbox & Nintendo', description: 'Shop checked new, pre-owned and retro consoles in Cosham, including PlayStation, Xbox and Nintendo systems, controllers and accessories.', image: '/images/2026/categories/consoles/hero.webp' },
  '/games': { title: 'Video Games in Cosham | New, Pre-Owned & Retro', description: 'Buy and trade new, pre-owned and retro video games in Cosham for PlayStation, Xbox, Nintendo and classic systems.', image: '/images/2026/categories/games/hero.webp' },
  '/tech': { title: 'Phones, Laptops & Gaming Tech in Cosham | Game On', description: 'Buy and trade phones, laptops, tablets, headsets, controllers and gaming accessories locally at Game On Cosham.', image: '/images/2026/categories/tech/hero.webp' },
  '/trade-in': { title: 'Trade In Games & Tech for Cash in Cosham | Game On', description: 'Trade consoles, games, phones, laptops, PCs and accessories for cash or store credit at Game On Cosham in Portsmouth.', image: '/images/2026/categories/trade-in/hero.webp' },
  '/blog': { title: 'Gaming & Tech Guides | Game On Cosham Blog', description: 'Read gaming, console, technology and PC advice from the team at Game On Cosham.' }
};

const setMeta = (selector, attribute, value) => {
  const element = document.querySelector(selector);
  if (element) element.setAttribute(attribute, value);
};

const RouteMeta = () => {
  const { pathname } = useLocation();

  React.useEffect(() => {
    const meta = pageMeta[pathname];
    const isBlogPost = pathname.startsWith('/blog/');
    const title = meta?.title || (isBlogPost ? 'Game On Cosham Blog' : 'Page Not Found | Game On Cosham');
    const description = meta?.description || (isBlogPost ? pageMeta['/blog'].description : 'The page you requested could not be found. Explore Game On Cosham for games, consoles, technology and gaming PCs.');
    const canonical = `${SITE_URL}${pathname === '/' ? '/' : pathname.replace(/\/$/, '')}`;
    const image = meta?.image ? `${SITE_URL}${meta.image}` : DEFAULT_IMAGE;

    document.title = title;
    setMeta('meta[name="description"]', 'content', description);
    setMeta('meta[name="robots"]', 'content', meta || isBlogPost ? 'index, follow, max-image-preview:large' : 'noindex, follow');
    setMeta('link[rel="canonical"]', 'href', canonical);
    setMeta('meta[property="og:title"]', 'content', title);
    setMeta('meta[property="og:description"]', 'content', description);
    setMeta('meta[property="og:url"]', 'content', canonical);
    setMeta('meta[property="og:image"]', 'content', image);
    setMeta('meta[name="twitter:title"]', 'content', title);
    setMeta('meta[name="twitter:description"]', 'content', description);
    setMeta('meta[name="twitter:image"]', 'content', image);

    const routeSchema = document.getElementById('route-schema');
    if (routeSchema && meta && pathname !== '/') {
      routeSchema.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@graph': [
          { '@type': 'WebPage', '@id': `${canonical}#webpage`, url: canonical, name: title, description, isPartOf: { '@id': `${SITE_URL}/#website` }, primaryImageOfPage: { '@type': 'ImageObject', url: image } },
          { '@type': 'BreadcrumbList', itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
            { '@type': 'ListItem', position: 2, name: title.split('|')[0].trim(), item: canonical }
          ] }
        ]
      });
    } else if (routeSchema) {
      routeSchema.textContent = '';
    }
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

const NotFound = () => (
  <section className="not-found" aria-labelledby="not-found-title">
    <p className="pixel-kicker">Continue?</p>
    <h1 id="not-found-title">Page not found</h1>
    <p>That level does not exist. Head back to the shop and choose another route.</p>
    <Link className="pixel-button" to="/">Return home</Link>
  </section>
);

function App() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showIntro, setShowIntro] = useState(shouldShowLoadingIntro);
  const isDarkMode = true;

  React.useEffect(() => {
    document.body.classList.add('dark-mode');
  }, []);

  React.useEffect(() => {
    document.body.classList.toggle('intro-active', showIntro);
    return () => document.body.classList.remove('intro-active');
  }, [showIntro]);

  const closeMenu = () => setIsMenuOpen(false);

  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <RouteMeta />
      {showIntro && <LoadingIntro onComplete={() => setShowIntro(false)} />}
      <div className="site-shell">
        <header className="site-header">
          <div className="arcade-brand-row">
            <div className="utility-socials" aria-label="Social links">
                <a href="https://www.facebook.com/gameoncosham" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                  <FontAwesomeIcon icon={faFacebookF} />
                </a>
                <a href="https://www.instagram.com/gameoncosham/?hl=en" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                  <FontAwesomeIcon icon={faInstagram} />
                </a>
                <a href="https://www.tiktok.com/@game_on_cosham" target="_blank" rel="noopener noreferrer" aria-label="TikTok">
                  <FontAwesomeIcon icon={faTiktok} />
                </a>
            </div>
            <Link className="brand-banner" to="/" onClick={closeMenu}>
              <img src="/images/2026/logo-header-transparent-2026.png" alt="Game On 15 - buy, sell, trade" />
            </Link>
            <div className="header-shortcuts" aria-label="Shop categories">
              <Link to="/consoles"><FontAwesomeIcon icon={faGamepad} /><span>Consoles</span></Link>
              <Link to="/tech"><FontAwesomeIcon icon={faMobileScreen} /><span>Phones</span></Link>
              <Link to="/pc-building"><FontAwesomeIcon icon={faDesktop} /><span>PC Building</span></Link>
            </div>
          </div>
          <nav className="main-nav arcade-nav" aria-label="Primary navigation">
            <button
              className="nav-toggle"
              type="button"
              aria-label="Toggle navigation"
              aria-controls="primary-nav-links"
              aria-expanded={isMenuOpen}
              onClick={() => setIsMenuOpen((open) => !open)}
            >
              <span></span>
              <span></span>
              <span></span>
            </button>
            <div id="primary-nav-links" className={`nav-links ${isMenuOpen ? 'is-open' : ''}`}>
              <NavLink to="/" onClick={closeMenu}>Home</NavLink>
              <NavLink to="/consoles" onClick={closeMenu}>Consoles</NavLink>
              <NavLink to="/pc-building" onClick={closeMenu}>PC Building</NavLink>
              <NavLink to="/games" onClick={closeMenu}>Games</NavLink>
              <NavLink to="/tech" onClick={closeMenu}>Tech</NavLink>
              <NavLink to="/trade-in" onClick={closeMenu}>Trade In</NavLink>
              <NavLink to="/about" onClick={closeMenu}>About</NavLink>
              <NavLink to="/history" onClick={closeMenu}>History</NavLink>
              <NavLink to="/contact" onClick={closeMenu}>Contact</NavLink>
            </div>
          </nav>
        </header>

        <main className="main-content-wrapper">
          <Routes>
            <Route path="/" element={<Home isDarkMode={isDarkMode} />} />
            <Route path="/about" element={<About isDarkMode={isDarkMode} />} />
            <Route path="/pc-building" element={<PCBuilding isDarkMode={isDarkMode} />} />
            <Route path="/contact" element={<Contact isDarkMode={isDarkMode} />} />
            <Route path="/history" element={<History isDarkMode={isDarkMode} />} />
            <Route path="/consoles" element={<CategoryPage type="consoles" />} />
            <Route path="/games" element={<CategoryPage type="games" />} />
            <Route path="/tech" element={<CategoryPage type="tech" />} />
            <Route path="/trade-in" element={<CategoryPage type="trade" />} />
            <Route path="/blog" element={<Blog isDarkMode={isDarkMode} />} />
            <Route path="/blog/:slug" element={<PostDetails isDarkMode={isDarkMode} />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </main>

        <footer className="site-footer">
          <div className="footer-brand">
            <img src="/images/2026/logo-header-transparent-2026.png" alt="Game On Cosham" />
            <div>
              <p className="pixel-kicker">Continue?</p>
              <p>Buy. Sell. Trade. Instant cash for your tech.</p>
            </div>
          </div>
          <div className="footer-meta">
            <span>&copy; {new Date().getFullYear()} Game On Cosham</span>
            <span>Consoles. Phones. Games. Gaming PCs.</span>
            <span>Designed by <a href="https://greypatrick.com" target="_blank" rel="noopener noreferrer">Grey Patrick</a></span>
          </div>
        </footer>
      </div>
    </Router>
  );
}

export default App;
